import config from '../config';
import type {
  CatalogEntity,
  CatalogEvent,
  CatalogEventDetail,
  CatalogEventInput,
  CatalogEventSource,
  CatalogMetadata,
  EntityEventLink,
  EntityEventSourceMapping,
  EntityInput,
  EntityMergeResult,
  EventSourceInput,
} from '../types/catalog';

type Query = Record<string, string | number | boolean | null | undefined>;

interface CatalogRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
}

function catalogUrl(path: string, query?: Query): string {
  const url = `${config.api.baseUrl}/catalog/${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const encoded = params.toString();
  return encoded ? `${url}?${encoded}` : url;
}

async function catalogFetch<T>(path: string, request: CatalogRequest = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(catalogUrl(path, request.query), {
      method: request.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      body: request.body === undefined ? undefined : JSON.stringify(request.body),
      signal: AbortSignal.timeout(config.api.timeout),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new Error(`Catalog request timed out after ${config.api.timeout / 1000} seconds`);
    }
    throw error;
  }

  let payload: any = null;
  try {
    payload = await response.json();
  } catch {
    // Some successful DELETE responses may not have a body.
  }

  if (!response.ok) {
    const referenceId = typeof payload?.referenceId === 'string' ? payload.referenceId : undefined;
    const message = payload?.message || payload?.error || `Request failed with status ${response.status}`;
    const error = new Error(
      referenceId ? `${message} Reference: ${referenceId}` : message
    ) as Error & { status?: number; code?: string; referenceId?: string };
    error.status = response.status;
    error.code = payload?.error;
    error.referenceId = referenceId;
    throw error;
  }
  return payload as T;
}

export const catalogApi = {
  getMetadata: () => catalogFetch<CatalogMetadata>('metadata'),

  listEntities: async (search = '', limit = 50): Promise<CatalogEntity[]> => {
    const data = await catalogFetch<{ entities: CatalogEntity[] }>('entities', {
      query: { search, limit },
    });
    return data.entities;
  },

  getEntity: async (entityId: number): Promise<CatalogEntity> => {
    const data = await catalogFetch<{ entity: CatalogEntity }>('entities', {
      query: { entityId },
    });
    return data.entity;
  },

  createEntity: async (input: EntityInput): Promise<CatalogEntity> => {
    const data = await catalogFetch<{ entity: CatalogEntity }>('entities', {
      method: 'POST',
      body: input,
    });
    return data.entity;
  },

  updateEntity: async (entityId: number, input: Partial<EntityInput>): Promise<CatalogEntity> => {
    const data = await catalogFetch<{ entity: CatalogEntity }>('entities', {
      method: 'PATCH',
      body: { entityId, ...input },
    });
    return data.entity;
  },

  /** Without `confirm` this reports what the merge would do and changes nothing. */
  mergeEntities: (
    survivorId: number,
    mergedIds: number[],
    options: { confirm?: boolean } = {}
  ): Promise<EntityMergeResult> => catalogFetch<EntityMergeResult>('entities/merge', {
    method: 'POST',
    body: { survivorId, mergedIds, confirm: options.confirm === true },
  }),

  listEventSources: async (search = '', limit = 50): Promise<CatalogEventSource[]> => {
    const data = await catalogFetch<{ eventSources: CatalogEventSource[] }>('eventSources', {
      query: { search, limit },
    });
    return data.eventSources;
  },

  getEventSource: async (eventSourceId: number): Promise<CatalogEventSource> => {
    const data = await catalogFetch<{ eventSource: CatalogEventSource }>('eventSources', {
      query: { eventSourceId },
    });
    return data.eventSource;
  },

  createEventSource: async (input: EventSourceInput): Promise<CatalogEventSource> => {
    const data = await catalogFetch<{ eventSource: CatalogEventSource }>('eventSources', {
      method: 'POST',
      body: input,
    });
    return data.eventSource;
  },

  updateEventSource: async (
    eventSourceId: number,
    input: Partial<EventSourceInput>
  ): Promise<CatalogEventSource> => {
    const data = await catalogFetch<{ eventSource: CatalogEventSource }>('eventSources', {
      method: 'PATCH',
      body: { eventSourceId, ...input },
    });
    return data.eventSource;
  },

  listEntityEventSources: async (filters: {
    entityId?: number;
    eventSourceId?: number;
  }): Promise<EntityEventSourceMapping[]> => {
    const data = await catalogFetch<{ mappings: EntityEventSourceMapping[] }>(
      'entityEventSources',
      { query: { ...filters, limit: 200 } }
    );
    return data.mappings;
  },

  linkEntityEventSource: async (
    entityId: number,
    eventSourceId: number
  ): Promise<EntityEventSourceMapping> => {
    const data = await catalogFetch<{ mapping: EntityEventSourceMapping }>(
      'entityEventSources',
      { method: 'POST', body: { entityId, eventSourceId, isActive: true } }
    );
    return data.mapping;
  },

  setEntityEventSourceActive: async (
    entityId: number,
    eventSourceId: number,
    isActive: boolean
  ): Promise<EntityEventSourceMapping> => {
    const data = await catalogFetch<{ mapping: EntityEventSourceMapping }>(
      'entityEventSources',
      { method: 'PATCH', body: { entityId, eventSourceId, isActive } }
    );
    return data.mapping;
  },

  unlinkEntityEventSource: (entityId: number, eventSourceId: number) =>
    catalogFetch<{ deleted: boolean }>('entityEventSources', {
      method: 'DELETE',
      body: { entityId, eventSourceId },
    }),

  listEvents: async (filters: {
    search?: string;
    entityId?: number;
    eventSourceId?: number;
    pastDays?: number;
    limit?: number;
  } = {}): Promise<CatalogEvent[]> => {
    const data = await catalogFetch<{ events: CatalogEvent[] }>('events', {
      query: filters,
    });
    return data.events;
  },

  getEvent: (eventId: number) =>
    catalogFetch<CatalogEventDetail>('events', { query: { eventId } }),

  updateEvent: async (eventId: number, input: CatalogEventInput): Promise<CatalogEvent> => {
    const data = await catalogFetch<{ event: CatalogEvent }>('events', {
      method: 'PATCH',
      body: { eventId, ...input },
    });
    return data.event;
  },

  linkEntityEvent: async (entityId: number, eventId: number): Promise<EntityEventLink> => {
    const data = await catalogFetch<{ link: EntityEventLink }>('entityEvents', {
      method: 'POST',
      body: { entityId, eventId },
    });
    return data.link;
  },

  unlinkEntityEvent: async (entityId: number, eventId: number): Promise<EntityEventLink> => {
    const data = await catalogFetch<{ link: EntityEventLink }>('entityEvents', {
      method: 'DELETE',
      body: { entityId, eventId },
    });
    return data.link;
  },
};